import { createWrapper } from "@segment/analytics-consent-tools";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { getInvoiceIdFromCPEPath } from "../ClientPayments/utils";
import {
  CONSENT_CHANGED_EVENT,
  CONSENT_GATED_PATHS
} from "../modules/CookieConsent/constants";
import { getCurrentPreferences } from "../modules/CookieConsent/utils/consentManager";
import { useFeatureFlags } from "../query/hooks/useFeatureFlags";
import { getRequestingUserSessionUserId } from "../services/sessionStorage";
import { BUILD_ENV, IS_DEV_ENV } from "../shared/constants/environment";
import { shouldSendAnalyticsCall } from "./analyticsGate";
import { localStorageUtil } from "./localStorage";
import {
  identifyPostHog,
  resetPostHog,
  syncPostHogSessionReplay
} from "./postHog";
import { matchPath, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useCookieConsent } from "../modules/CookieConsent";
import { useQueryUserProfileV2 } from "../query/user/queries/useQueryUserProfile";

const SEGMENT_CDN_URL = "https://analytics-cdn.wingspan.app";

const analytics = new AnalyticsBrowser();

export const isAnalyticsLoaded = (): boolean => !!analytics.instance;

export const DEBUG_ANALYTICS =
  IS_DEV_ENV || window.debugSegment || window.TEST_ENV;

const getCategories = (): Record<string, boolean> => {
  const { C0001MT, C0002AL } = getCurrentPreferences();
  return { C0001MT, C0002AL };
};

// Opt-in model: device-mode destinations whose categories are not consented
// are removed from the CDN settings before they load. Cloud-mode destinations
// still receive events stamped with the consent context for server-side filtering.
// Category → destination mapping is configured in the Segment workspace
// (Privacy → Consent Management).
const withConsent = createWrapper<AnalyticsBrowser>({
  getCategories,
  registerOnConsentChanged: setCategories => {
    document.addEventListener(CONSENT_CHANGED_EVENT, () => {
      setCategories(getCategories());

      if (isAnalyticsLoaded()) {
        //In case if analytic was loaded on previous page, let's reload to make sure that it works properly
        window.location.reload();
      }
    });
  },
  shouldLoadSegment: ctx => ctx.load({ consentModel: "opt-in" })
});

export const useInitAnalytics = (
  isSessionFetched: boolean,
  isSessionActive: boolean,
  userId?: string
) => {
  const location = useLocation();
  const { hasDecision, loadUserPrivacyPreferences } = useCookieConsent();
  const [isReadyToLoad, setIsReadyToLoad] = useState(false);
  // Session replay is an authenticated experiment, so the assignment is only
  // requested for an active session. Anonymous visitors would otherwise send an
  // unauthenticated /experiments request on every public page view.
  const queryFeatureFlags = useFeatureFlags({
    enabled: isSessionFetched && isSessionActive
  });

  const userProfile = useQueryUserProfileV2({
    enabled: isSessionFetched && isSessionActive,
    onSuccess: user => {
      // Resolve the internal-user exclusion before either analytics SDK can
      // initialize for an authenticated session.
      window.userEmail = user.email || "";
      loadUserPrivacyPreferences(user.privacyPreferences);
    }
  });

  const isConsentGatedPath = !!matchPath(location.pathname, {
    path: [...CONSENT_GATED_PATHS]
  });

  const isUserDataFetched = useMemo(() => {
    if (isSessionFetched && !isSessionActive) {
      return true;
    }

    return isSessionFetched && isSessionActive && userProfile.isFetched;
  }, [isSessionFetched, isSessionActive, userProfile.isFetched]);

  useEffect(() => {
    let timeout = 0;

    if (isUserDataFetched) {
      // We should wait before all redirects complete
      timeout = window.setTimeout(() => {
        setIsReadyToLoad(true);
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isUserDataFetched]);

  useEffect(() => {
    if (hasDecision) {
      loadSegment();
    } else if (isReadyToLoad && !isConsentGatedPath) {
      loadSegment();
    }
  }, [isReadyToLoad, hasDecision, isConsentGatedPath]);

  useEffect(() => {
    const canLoadAnalytics =
      hasDecision || (isReadyToLoad && !isConsentGatedPath);

    if (!isReadyToLoad || !queryFeatureFlags.isFetched || !canLoadAnalytics) {
      resetPostHog();
      return;
    }

    syncPostHogSessionReplay({
      enabled: !!queryFeatureFlags.data?.postHogSessionReplay,
      userId
    });
  }, [
    hasDecision,
    isConsentGatedPath,
    isReadyToLoad,
    queryFeatureFlags.data?.postHogSessionReplay,
    queryFeatureFlags.isFetched,
    userId
  ]);

  return null;
};

export const loadSegment = (): void => {
  if (isAnalyticsLoaded()) return;

  try {
    const shouldSend = shouldSendAnalyticsCall();
    if (shouldSend && process.env.REACT_APP_SEGMENT_API_KEY) {
      withConsent(analytics).load(
        {
          cdnURL: SEGMENT_CDN_URL,
          writeKey: process.env.REACT_APP_SEGMENT_API_KEY
        },
        { initialPageview: false }
      );

      page();

      if (DEBUG_ANALYTICS) {
        console.info(
          "Successful loading Segment.io, categories: ",
          JSON.stringify(getCategories(), null, 2)
        );
      }
    }
  } catch (error) {
    if (DEBUG_ANALYTICS) {
      console.error("Failed loading Segment.io");
      console.error(error);
    }
  }
};

export const SEGMENT_NOT_LOADED = "SEGMENT_NOT_LOADED";

export const getSegmentAnonymousIdFromLocalStorage = (): string => {
  const id =
    localStorageUtil.getItem("ajs_anonymous_id") || `"${SEGMENT_NOT_LOADED}"`;
  return id.split('"')[1];
};

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

const buildEnv = BUILD_ENV;
const buildDate = new Date(
  `${process.env.REACT_APP_BUILD_TIMESTAMP}`
).toLocaleString(undefined, { hour12: false });
const buildCommit = process.env.REACT_APP_GIT_LATEST_COMMIT;

function getCommonFields() {
  const deviceType = getDeviceType();
  return {
    context: {
      wingspan: {
        // enabledFeatureFlags: getEnabledFeatureFlags(state),
        buildCommit,
        buildDate,
        buildEnv,
        deviceType
      }
    }
  };
}

export function page(
  name: string | undefined = undefined,
  properties: any = {}
) {
  const customProperties = {
    invoiceId: getInvoiceIdFromCPEPath(window.location.pathname)
  };
  const requestingUserId = getRequestingUserSessionUserId();
  const commonPageProps = {
    ...(requestingUserId ? { requestingUserId } : {})
  };
  const eventName = name;
  const eventProperties = {
    ...commonPageProps,
    ...properties,
    ...customProperties
  };
  const eventOptions = getCommonFields();
  const shouldSend = shouldSendAnalyticsCall();
  if (DEBUG_ANALYTICS) {
    console.info(
      `Analytics Page call for ${getSegmentAnonymousIdFromLocalStorage()} ${
        shouldSend ? "sent" : "not sent"
      }: ${JSON.stringify([eventName, eventProperties, eventOptions])}`
    );
  }

  if (shouldSend) {
    analytics.page(eventName, eventProperties, eventOptions);
  }
}

export function identifyWithCommonFields(
  userId: string | undefined = undefined,
  traits: any = {}
) {
  try {
    const shouldSend = shouldSendAnalyticsCall();
    const eventTraits = { ...traits };
    // Omit the Intercom override entirely until a credential is available
    // (e.g. an identify fired right after signup, before useIdentify's
    // profile queries resolve): Segment merges successive identify calls
    // into one Intercom session, so sending an explicit undefined user_hash
    // poisons it, while leaving the key out merges harmlessly.
    const hasIntercomCredential = !!(
      window.intercomUserJwt || window.intercomUserHash
    );
    const eventOptions = {
      ...getCommonFields(),
      ...(hasIntercomCredential
        ? {
            integrations: {
              Intercom: window.intercomUserJwt
                ? { intercom_user_jwt: window.intercomUserJwt }
                : { user_hash: window.intercomUserHash }
            }
          }
        : {})
    };
    if (DEBUG_ANALYTICS) {
      console.info(
        `Analytics Identify call for ${getSegmentAnonymousIdFromLocalStorage()} ${
          shouldSend ? "sent" : "not sent"
        }: ${JSON.stringify([userId, eventTraits, eventOptions])}`
      );
    }
    if (shouldSend) {
      analytics.identify(userId, eventTraits, eventOptions);
      identifyPostHog(userId);
    } else {
      // PostHog may have started anonymously before the employee/admin identity
      // was known, so stop it as soon as the shared exclusion policy applies.
      resetPostHog();
    }
  } catch (error) {
    console.error("IDENTIFY ERROR:", error);
  }
}

export function track(
  event: string | undefined = undefined,
  properties: any = {}
) {
  const shouldSend = shouldSendAnalyticsCall();
  const commonFields = getCommonFields();
  const requestingUserId = getRequestingUserSessionUserId();
  const commonTrackProps = {
    ...(requestingUserId ? { requestingUserId } : {})
  };
  const eventProperties = { ...commonTrackProps, ...properties };
  if (DEBUG_ANALYTICS) {
    console.info(
      `Analytics Track call for ${getSegmentAnonymousIdFromLocalStorage()} ${
        shouldSend ? "sent" : "not sent"
      }: ${JSON.stringify([event, eventProperties, commonFields])}`
    );
  }

  if (shouldSend && event) {
    analytics.track(event, eventProperties, commonFields);
  }
}

export function reset() {
  // just wrap the function in our own fun(ction)
  if (DEBUG_ANALYTICS) {
    console.info(`Analytics reset`);
    console.info("Analytics anon id", getSegmentAnonymousIdFromLocalStorage());
  }
  analytics.reset();
  resetPostHog();
}

export function group(groupId: string, traits?: any) {
  const shouldSend = shouldSendAnalyticsCall();

  if (shouldSend) {
    analytics.group(groupId, traits);
  }
}
